import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PackagePlus, Pause, Play, CalendarPlus, UploadCloud, CheckCircle2, Clock } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { api, money, prettyDate, prettyTs, fileToDataUrl } from "./api";

const STATUS_STYLES = {
  pending_approval: "border-yellow-500/40 bg-yellow-400/10 text-yellow-700",
  active: "border-emerald-500/40 bg-emerald-400/10 text-emerald-700",
  paused: "border-orange-500/40 bg-orange-400/10 text-orange-700",
  archived: "border-black/20 bg-black/5 text-muted",
};
const FULFILL_STYLES = {
  ordered: "border-sky-500/40 bg-sky-400/10 text-sky-700",
  provider_assigned: "border-sky-500/40 bg-sky-400/10 text-sky-700",
  confirmed: "border-aqua/50 bg-aqua/10 text-aqua",
  in_progress: "border-yellow-500/40 bg-yellow-400/10 text-yellow-700",
  completed: "border-emerald-500/40 bg-emerald-400/10 text-emerald-700",
  delivered: "border-emerald-500/40 bg-emerald-400/10 text-emerald-700",
  cancelled: "border-black/20 bg-black/5 text-muted",
  refunded: "border-red-500/40 bg-red-400/10 text-red-600",
};
const cents = (n) => `$${((n || 0) / 100).toFixed(2)}`;
const nextStatus = { ordered: "provider_assigned", provider_assigned: "confirmed", confirmed: "in_progress", in_progress: "completed", completed: "delivered" };

const emptyProposal = { name: "", category: "photography", description: "", price: "", locations: [], documents: {}, images: [], tax_pct: 0, delivery_timeframe: "" };

export default function PortalUpsells() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("orders");
  const [form, setForm] = useState(emptyProposal);
  const [docInputs, setDocInputs] = useState({});
  const [busy, setBusy] = useState(false);
  const [evidence, setEvidence] = useState({});

  const load = useCallback(() => {
    api.get("/portal/upsells").then(({ data }) => setData(data)).catch((e) => toast.error(e?.response?.data?.detail || "Could not load upsells"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const propose = () => act(() => api.post("/portal/upsells", { ...form, price: parseFloat(form.price), tax_pct: parseFloat(form.tax_pct) || 0, locations: form.locations.length ? form.locations : undefined }), "Proposal submitted for XE approval");

  const addDoc = (key, label) => {
    const val = docInputs[key]?.trim();
    if (!val) return;
    setForm((f) => ({ ...f, documents: { ...f.documents, [key]: val } }));
    setDocInputs((d) => ({ ...d, [key]: "" }));
  };

  const addEvidence = async (orderId, upsellId, files) => {
    const urls = await Promise.all(Array.from(files).slice(0, 3).map(fileToDataUrl));
    const st = evidence[`${orderId}:${upsellId}`] || {};
    await act(() => api.post(`/portal/upsell-orders/${orderId}/items/${upsellId}/evidence`, { photos: urls, links: st.link ? [st.link] : [], download_link: st.download || "", note: st.note || "" }), "Evidence uploaded");
  };

  if (!data) return <PortalShell title="Upsells"><p className="py-20 text-center text-sm text-muted">Loading…</p></PortalShell>;

  const myOrders = data.orders.filter((o) => o.payment_status === "paid");

  return (
    <PortalShell title="Upsells">
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-aqua/40 bg-aqua/[0.07] p-5">
        <PackagePlus size={18} className="mt-0.5 shrink-0 text-aqua" />
        <p className="text-sm leading-relaxed text-offwhite">
          Propose upsells and manage availability — XE approves every upsell, sets final prices and splits, and collects
          all payments through XE checkout. You receive <strong>100% of net revenue</strong> (after taxes, Stripe fees,
          refunds) for upsells you provide and fulfill, released after the service is marked completed or delivered with
          evidence. Upsells never count as Jet Car rental hours and never affect your weekly revenue tier.
        </p>
      </div>

      <div className="mb-6 flex gap-2">
        {[["orders", "Orders & Fulfillment"], ["mine", "My Upsells"], ["propose", "Propose New Upsell"]].map(([id, label]) => (
          <button key={id} onClick={() => setView(id)} data-testid={`upsell-view-${id}`}
            className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-widest ${view === id ? "border-aqua bg-aqua/15 text-aqua" : "border-black/10 text-muted"}`}>
            {label}
          </button>
        ))}
      </div>

      {view === "orders" && (
        <div className="space-y-4" data-testid="upsell-orders">
          {myOrders.length === 0 && <p className="card-line rounded-xl py-12 text-center text-sm text-muted" data-testid="orders-empty">No upsell orders yet.</p>}
          {myOrders.map((o) => (
            <div key={o.order_id} className="card-line rounded-xl p-5" data-testid={`order-${o.order_id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-xs text-aqua">{o.order_id}</span>
                <span className="text-sm font-semibold text-offwhite">{o.customer.name}</span>
                <span className="text-xs text-muted">Reservation {o.reservation_id} · service {prettyDate(o.service_date)}</span>
                <span className="ml-auto text-xs text-muted">{cents(o.total_cents)}</span>
              </div>
              <div className="mt-4 space-y-3">
                {o.items.filter((i) => i.provider_partner_id === o.partner_id).map((it) => {
                  const key = `${o.order_id}:${it.upsell_id}`;
                  const st = evidence[key] || {};
                  const net = it.amount_cents - it.tax_cents - it.fee_alloc_cents - it.refunds_cents;
                  return (
                    <div key={it.upsell_id} className="rounded-lg border border-black/[0.08] bg-ink/40 p-4" data-testid={`order-item-${key}`}>
                      <div className="flex flex-wrap items-center gap-2.5">
                        <p className="text-sm font-bold text-offwhite">{it.name} × {it.qty}</p>
                        <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${FULFILL_STYLES[it.status]}`} data-testid={`fulfill-status-${key}`}>{it.status.replace("_", " ")}</span>
                        {it.released
                          ? <span className="rounded-full border border-emerald-500/40 bg-emerald-400/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-700">Payout released</span>
                          : <span className="rounded-full border border-orange-500/40 bg-orange-400/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-orange-700">Pending release</span>}
                        <span className="ml-auto text-xs text-muted">net {cents(net)} · yours 100% = <strong className="text-aqua">{cents(net)}</strong></span>
                      </div>
                      <p className="mt-1.5 text-[11px] text-muted">gross {cents(it.amount_cents)} − tax {cents(it.tax_cents)} − Stripe fee {cents(it.fee_alloc_cents)} − refunds {cents(it.refunds_cents)}</p>
                      {!["cancelled", "refunded", "delivered"].includes(it.status) && (
                        <div className="mt-3 space-y-2.5 border-t border-black/[0.08] pt-3">
                          <div className="flex flex-wrap gap-2">
                            {nextStatus[it.status] && (
                              <button disabled={busy} onClick={() => act(() => api.post(`/portal/upsell-orders/${o.order_id}/items/${it.upsell_id}/fulfill`, { status: nextStatus[it.status] }), `Marked ${nextStatus[it.status].replace("_", " ")}`)}
                                className="btn-primary !px-4 !py-2 !text-xs" data-testid={`fulfill-next-${key}`}>
                                Mark {nextStatus[it.status].replace("_", " ")}
                              </button>
                            )}
                          </div>
                          <div className="grid gap-2 sm:grid-cols-3">
                            <input className="field !py-2 text-xs" placeholder="Delivery / gallery link (https://…)" value={st.download || ""} onChange={(e) => setEvidence((s) => ({ ...s, [key]: { ...s[key], download: e.target.value } }))} data-testid={`evidence-link-${key}`} />
                            <input className="field !py-2 text-xs" placeholder="Completion note" value={st.note || ""} onChange={(e) => setEvidence((s) => ({ ...s, [key]: { ...s[key], note: e.target.value } }))} data-testid={`evidence-note-${key}`} />
                            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted">
                              <UploadCloud size={14} className="text-aqua" /> Proof photos
                              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files.length && addEvidence(o.order_id, it.upsell_id, e.target.files)} data-testid={`evidence-photos-${key}`} />
                            </label>
                          </div>
                          {(st.download || st.note) && (
                            <button disabled={busy} onClick={() => addEvidence(o.order_id, it.upsell_id, [])} className="btn-ghost !px-4 !py-2 !text-xs" data-testid={`evidence-save-${key}`}>Save evidence</button>
                          )}
                        </div>
                      )}
                      {it.evidence?.length > 0 && <p className="mt-2 text-[11px] text-emerald-700"><CheckCircle2 size={11} className="mr-1 inline" />{it.evidence.length} evidence upload{it.evidence.length > 1 ? "s" : ""} on file</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "mine" && (
        <div className="space-y-4" data-testid="my-upsells">
          {data.upsells.length === 0 && <p className="card-line rounded-xl py-12 text-center text-sm text-muted">No upsells assigned to your locations yet.</p>}
          {data.upsells.map((u) => (
            <div key={u.upsell_id} className="card-line rounded-xl p-5" data-testid={`my-upsell-${u.upsell_id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-display text-base font-bold text-offwhite">{u.name}</p>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted">{u.category.replace("_", " ")}</span>
                <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[u.status]}`}>{u.status.replace("_", " ")}</span>
                {u.paused && <span className="rounded-full border border-orange-500/40 bg-orange-400/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-orange-700">Paused</span>}
                <span className="ml-auto font-display text-lg font-extrabold text-offwhite">${u.pricing?.base_price}</span>
              </div>
              <p className="mt-1.5 text-xs text-muted">{u.description}</p>
              <p className="mt-1 text-[11px] text-muted/70">
                Provider: {u.provider_type === "partner" ? "You" : u.provider_type.toUpperCase()} · split {u.split?.provider_pct}/{u.split?.xe_pct} (provider/XE)
                {u.availability_windows?.length > 0 && ` · available ${u.availability_windows.map((w) => `${prettyDate(w.start)}→${prettyDate(w.end)}`).join(", ")}`}
              </p>
              {u.provider_type === "partner" && u.status !== "archived" && (
                <div className="mt-3 flex flex-wrap gap-2 border-t border-black/[0.08] pt-3">
                  <button disabled={busy} onClick={() => act(() => api.post(`/portal/upsells/${u.upsell_id}/pause`, { paused: !u.paused, reason: u.paused ? "" : "Provider unavailable" }), u.paused ? "Availability resumed" : "Availability paused")}
                    className="btn-ghost !px-4 !py-2 !text-xs" data-testid={`pause-${u.upsell_id}`}>
                    {u.paused ? <><Play size={12} /> Resume availability</> : <><Pause size={12} /> Pause availability</>}
                  </button>
                  <AvailabilityEditor upsell={u} onSave={(windows) => act(() => api.post(`/portal/upsells/${u.upsell_id}/availability`, { availability_windows: windows }), "Availability updated")} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {view === "propose" && (
        <div className="card-line max-w-2xl rounded-xl p-6" data-testid="propose-form">
          <h2 className="mb-1 font-display text-base font-bold text-offwhite">Propose a New Upsell</h2>
          <p className="mb-4 text-[11px] leading-relaxed text-muted/70">XE reviews every proposal, sets the final customer-facing price and split, and publishes it. You cannot publish or change prices directly.</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1"><label className="field-label" htmlFor="up-name">Service name</label><input id="up-name" className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="upsell-name" /></div>
              <div className="col-span-2 sm:col-span-1">
                <label className="field-label" htmlFor="up-cat">Category</label>
                <select id="up-cat" className="field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, documents: {} })} data-testid="upsell-category">
                  {data.categories.map((c) => <option key={c} value={c} className="bg-white">{c.replace("_", " ")}</option>)}
                </select>
              </div>
            </div>
            <div><label className="field-label" htmlFor="up-desc">Description (customer-facing after XE approval)</label><textarea id="up-desc" rows={3} className="field resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="upsell-description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="field-label" htmlFor="up-price">Recommended price (USD)</label><input id="up-price" type="number" min="1" className="field" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="upsell-price" /></div>
              <div><label className="field-label" htmlFor="up-delivery">Delivery timeframe</label><input id="up-delivery" className="field" placeholder="e.g. within 72 hours" value={form.delivery_timeframe} onChange={(e) => setForm({ ...form, delivery_timeframe: e.target.value })} data-testid="upsell-delivery" /></div>
            </div>
            {form.category === "drone_footage" && (
              <div className="rounded-lg border border-aqua/40 bg-aqua/[0.06] p-4" data-testid="drone-requirements">
                <p className="mb-2 text-xs font-bold uppercase tracking-widest text-aqua">Drone compliance — required before activation</p>
                <p className="mb-3 text-[11px] leading-relaxed text-muted">{data.drone_notices}</p>
                {data.drone_required_docs.map((d) => (
                  <div key={d} className="mb-2 flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${form.documents[d] ? "bg-emerald-500" : "bg-orange-500"}`} />
                    <span className="flex-1 text-xs capitalize text-offwhite">{d.replace(/_/g, " ")}</span>
                    <input className="field w-64 !py-1.5 text-xs" placeholder="Link or reference + expiry" value={docInputs[d] || ""} onChange={(e) => setDocInputs((s) => ({ ...s, [d]: e.target.value }))} data-testid={`drone-doc-${d}`} />
                    <button onClick={() => addDoc(d)} className="btn-ghost !px-3 !py-1.5 !text-[10px]" data-testid={`drone-doc-add-${d}`}>Add</button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={propose} disabled={busy || !form.name.trim() || !form.price} className="btn-primary !py-2.5 !text-xs disabled:opacity-40" data-testid="upsell-propose-submit">
              {busy ? "Submitting…" : "Submit Proposal to XE"}
            </button>
          </div>
        </div>
      )}
    </PortalShell>
  );
}

function AvailabilityEditor({ upsell, onSave }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const windows = upsell.availability_windows || [];
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <button onClick={() => setOpen(!open)} className="btn-ghost !px-4 !py-2 !text-xs" data-testid={`availability-toggle-${upsell.upsell_id}`}>
        <CalendarPlus size={12} /> Availability windows
      </button>
      {open && (
        <span className="inline-flex flex-wrap items-center gap-2">
          <input type="date" className="field w-auto !py-1.5 text-xs" value={start} onChange={(e) => setStart(e.target.value)} data-testid={`avail-start-${upsell.upsell_id}`} />
          <input type="date" className="field w-auto !py-1.5 text-xs" value={end} onChange={(e) => setEnd(e.target.value)} data-testid={`avail-end-${upsell.upsell_id}`} />
          <button onClick={() => { if (start && end && start <= end) onSave([...windows, { start, end }]); }} className="btn-primary !px-3 !py-1.5 !text-[10px]" data-testid={`avail-add-${upsell.upsell_id}`}>Add</button>
          {windows.length > 0 && <button onClick={() => onSave([])} className="text-[11px] font-semibold text-red-600" data-testid={`avail-clear-${upsell.upsell_id}`}>Clear</button>}
        </span>
      )}
    </span>
  );
}
